import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  KompisImage,
  KompisProductCard,
  KompisButton,
  KompisPopover,
  KompisPopoverPosition,
  KompisPopoverPadding,
  KompisIconButton,
  KompisOpenDesignCode,
  KompisText,
} from '@inter-ikea-kompis/react-components';
import { SkapaTheme } from '@inter-ikea-kompis/themes';
import { ThemeFontStyleTypeEnum } from '@inter-ikea-kompis/enums';
import styles from './StartView.module.less';
import {
  ProductCardLayoutEnum,
  ProductCardSelectBehaviourEnum,
  ProductCardMediaEnum,
} from '@inter-ikea-kompis/component-product-card';
import { selectMostRecentHistory } from '../../state/navigation/navigationSelectors';
import masterConstants from '../../settings/masterConstants';
import { nextView } from '../../state/navigation';
import useVpc from '../../hooks/useVpc';
import { selectSprsToDisplay } from '../../state/sprs/sprsSelectors';
import { thunkLoadSpr, thunkResetScene } from '../../state/tac/tacThunks';
import constants from '../../settings/constants';
import { translate } from '../../services/L10n';
import platform from '../../util/platform';
import {
  ButtonTypeEnum,
  ButtonSizeEnum,
} from '@inter-ikea-kompis/component-button';
import { IconButtonSizeEnum } from '@inter-ikea-kompis/component-icon-button';
import PopoverDirectionEnum from '@inter-ikea-kompis/component-popover/lib/enums/PopoverDirectionEnum';
import deviceTypes from '../../util/deviceTypes';
import { selectDeviceType } from '../../state/userAgent/userAgentSelectors';
import classNames from 'classnames';
import { ArrowRightIcon, ArrowLeftIcon } from '@inter-ikea-kompis/icons';
import { selectKompisTranslations } from '../../state/translations/translationsSelectors';
import {
  selectDexfSettings,
  selectUseMetric,
  selectIsRtl,
} from '../../state/dexfSettings/dexfSettingsSelectors';
import { selectScreensaverIsActive } from '../../state/screensaver/screensaverSelectors';
import {
  DesignInteractionEnum,
  DesignSourceEnum,
} from '@insights/insights-data-provider';
import { selectTacIsEmpty } from '../../state/tac/tacReducer/tacSelectors';
import OpenSeriesGalleryButton from '../../components/OpenSeriesGalleryButton';
import shouldShowOpenSeriesGalleryButton from '../../components/utils/seriesGallery';
import mandatoryStatisticsReporter from '../../services/statistics/insights/mandatory/mandatoryStatisticsReporter';
import localStatisticsReporter from '../../services/statistics/insights/custom/local/localStatisticsReporter';
import { applicationSettings } from '../../settings/application';
import LanguageSelector from '../../components/TopBar/LanguageSelector';
import { ProductDescriptionProductMeasureEnum } from '@inter-ikea-kompis/utilities/lib';
import { SprObject } from '../../state/sprs/sprsTypes';
import { useKioskIntegration } from '../../hooks/useKioskIntegration';
import { t } from '../../translations';

const StartView = () => {
  const sprs = useSelector(selectSprsToDisplay);
  const kompisTranslations = useSelector(selectKompisTranslations);
  const dexfSettings = useSelector(selectDexfSettings);
  const shouldUseMetric = useSelector(selectUseMetric);
  const isRtl = useSelector(selectIsRtl);
  const mostRecentHistory = useSelector(selectMostRecentHistory);
  const deviceType = useSelector(selectDeviceType);
  const screensaverActive = useSelector(selectScreensaverIsActive);
  const tacIsEmpty = useSelector(selectTacIsEmpty);

  const { isUpptacka } = useKioskIntegration();

  const dispatch = useDispatch();
  const resetScene = () => dispatch(thunkResetScene());
  const loadSpr = (spr: object) => dispatch(thunkLoadSpr(spr));
  const goToScene = () => dispatch(nextView());

  const [showVpcInput, setShowVpcInput] = React.useState(false);
  const mobilePortraitDeviceType = `${deviceTypes.MOBILE}_portrait`;
  const APPLICATION_NAME = applicationSettings.applicationName;

  /**
   * Handles response where pac was valid
   *
   * @param vpcConfig
   * @param pac { vpc, pac }
   */
  const handleValidPac = ({ pac }: any) => {
    loadSpr(pac);
  };

  const { vpcLoadingState, fetchVpc, resetHook } = useVpc({
    successCallback: handleValidPac,
  });

  /**
   * Resets start view
   *
   * @private
   */
  const _resetHook = () => {
    resetHook();
    setShowVpcInput(false);
  };

  React.useEffect(() => {
    screensaverActive && _resetHook();
  }, [screensaverActive]);

  const sprsPerRowBasedOnDevice = {
    [deviceTypes.MOBILE]: 1,
    [mobilePortraitDeviceType]: 2,
    [deviceTypes.TABLET]: 3,
    [deviceTypes.DESKTOP]: 4,
  };

  /**
   * Generate an aria label for an SPR product.
   *
   * @param {*} product The SPR product for which to generate an aria label.
   * @returns {string} The generated aria label.
   * @private
   */
  const _getProductCardAriaLabel = (product: SprObject) => {
    if (!product?.kompisSPR?.content) return '';

    const { typeName, validDesignText, measureReference } =
      product.kompisSPR.content;
    const measureReferenceString = shouldUseMetric
      ? measureReference?.textMetric
      : measureReference?.textImperial;

    const textElements = [];
    typeName && textElements.push(typeName);
    validDesignText && textElements.push(validDesignText);
    measureReferenceString && textElements.push(measureReferenceString);

    const ariaLabel = textElements.join(', ');

    return ariaLabel;
  };

  /**
   * Handles display setting for VPC input on user input
   *
   * @private
   */
  const _setShowVpcInput = () => setShowVpcInput(!showVpcInput);

  /**
   * Fetches and opens VPC
   *
   * @returns {Promise<void>}
   */
  const openDesign = (event: CustomEvent) => {
    const { vpcCode } = event.detail;
    fetchVpc(vpcCode);
  };

  /**
   * Resets and routes to scene
   */
  const startClean = () => {
    mandatoryStatisticsReporter.reportInitialPlanningSession(
      '',
      DesignSourceEnum.startFromScratch
    );
    resetScene();
  };

  /**
   * Returns true for all but the last row of products
   *
   * @param index
   * @returns {boolean}
   */
  const shouldHaveBorder = (index: number) =>
    sprs.length + 1 - index >
    (sprs.length + 1) % sprsPerRowBasedOnDevice[deviceType];

  /**
   * Returns correct card wrapper classes
   *
   * @param index
   * @returns {string}
   */
  const getCardWrapperClasses = (index: number) =>
    classNames(styles.kompisProductCardWrapper, {
      [styles.kompisProductCardWrapper__noBorder]: !shouldHaveBorder(index),
    });

  /**
   * On user click
   *
   * @param spr
   * @returns {function(): void}
   * @private
   */
  const _onClick = (spr: { vpcCode: string; id: string }) => () => {
    loadSpr(spr);

    mandatoryStatisticsReporter.reportDesignInteractionEvent(
      spr.vpcCode,
      DesignInteractionEnum.getConfiguration
    );
    mandatoryStatisticsReporter.reportInitialPlanningSession(
      spr.vpcCode,
      DesignSourceEnum.gallery
    );
    localStatisticsReporter.reportStartViewSprClick(spr.id);
  };

  /**
   * Render VPC input
   *
   * @returns {JSX.Element}
   */
  const renderVpcInput = () => (
    <KompisPopoverPosition>
      <KompisButton
        label={translate(t.LOAD_VPC_DIALOG_HEADING)}
        theme={SkapaTheme}
        type={ButtonTypeEnum.secondary}
        onClick={_setShowVpcInput}
        size={ButtonSizeEnum.small}
        ariaLabel={translate(t.SPR_GALLERY_OPEN_SAVED_SPR)}
        data-testid="open-vpc-button"
      />
      <KompisPopover
        visible={showVpcInput}
        theme={SkapaTheme}
        direction={PopoverDirectionEnum.bottom}
      >
        <div className={styles.startViewPopoverInnerWrapper}>
          <KompisPopoverPadding theme={SkapaTheme}>
            <KompisOpenDesignCode
              onOpen={openDesign}
              state={vpcLoadingState}
              translations={kompisTranslations}
              theme={SkapaTheme}
              data-testid="vpc-input-area"
            />
          </KompisPopoverPadding>
        </div>
      </KompisPopover>
    </KompisPopoverPosition>
  );

  /**
   * Renders a wrapper around a card
   *
   * @param child
   * @param index
   * @param key
   * @returns {JSX.Element}
   */
  const renderCardWrapper = (
    child: {} | null | undefined,
    index = 0,
    key = ''
  ): JSX.Element => (
    <div className={getCardWrapperClasses(index)} {...(key && { key })}>
      {child}
    </div>
  );

  /**
   * Render product card
   *
   * @param product
   * @param index
   * @returns {JSX.Element}
   */
  const renderProductCard = (product: SprObject, index: number): JSX.Element =>
    renderCardWrapper(
      <KompisProductCard
        product={product.kompisSPR}
        theme={SkapaTheme}
        productMeasures={ProductDescriptionProductMeasureEnum.short}
        selectBehaviour={ProductCardSelectBehaviourEnum.selectable}
        layout={ProductCardLayoutEnum.gallery}
        translations={kompisTranslations}
        settings={dexfSettings}
        selectedStateAriaLabel={_getProductCardAriaLabel(product)}
        onSelect={_onClick(product)}
        data-testid={`spr-product-card-${index}`}
        media={ProductCardMediaEnum.switchImageOnHover}
      />,
      index + 1,
      product.kompisSPR.itemId
    );

  /**
   * Returns SPR button icon based on isRtl
   *
   * @returns {*}
   */
  const getSprButtonIcon = () => (isRtl ? ArrowLeftIcon : ArrowRightIcon);

  /**
   * Render go to SPR button
   *
   * @returns {boolean|JSX.Element}
   */
  const renderSprButton = () =>
    mostRecentHistory === masterConstants.VIEW_NAMES.SCENE &&
    !tacIsEmpty && (
      <KompisIconButton
        icon={getSprButtonIcon()}
        theme={SkapaTheme}
        type={ButtonTypeEnum.secondary}
        onClick={goToScene}
        size={IconButtonSizeEnum.small}
        ariaLabel={translate(t.GALLERY_RETURN_INFORMATION)}
      />
    );

  /**
   * Render OpenSeriesGallery button
   *
   * @returns {JSX.Element}
   */
  const renderOpenSeriesGalleryButton = () => {
    return <OpenSeriesGalleryButton useArrowButton={true} />;
  };

  /**
   * Render start from beginning
   *
   * @returns {JSX.Element}
   */
  const renderStartFromBeginning = () =>
    renderCardWrapper(
      <React.Fragment>
        <KompisImage
          className={styles.startViewStartFromScratchImage}
          //@ts-ignore Should be able to click on image
          onClick={startClean}
          alt={translate(t.START_AGAIN)}
          src={`${constants.IMAGE_ROOT}${constants.START_FROM_SCRATCH_IMAGE_FILENAME}`}
        />
        <KompisButton
          theme={SkapaTheme}
          onClick={startClean}
          data-testid="start-from-scratch"
          label={translate(t.START_FROM_SCRATCH)}
          size={ButtonSizeEnum.small}
          className={styles.startViewStartFromScratchButton}
        />
      </React.Fragment>
    );

  /**
   * Create product card. Index is incremented by one here, due to the "renderStartFromBeginning" which is
   * no included in the SPR's array, yet has to be accounted for when calculating which items should have a bottom-border.
   *
   * @returns {*}
   */
  const createProductCards = () => sprs.map(renderProductCard);

  /**
   * Render header
   *
   * @param title
   * @returns {JSX.Element}
   */
  const renderHeader = (title: {} | null | undefined) =>
    platform.isKiosk ? (
      <h1 className={styles.startViewSubHeader}>{title}</h1>
    ) : (
      <KompisText
        type={ThemeFontStyleTypeEnum.headingMedium}
        theme={SkapaTheme}
      >
        {title}
      </KompisText>
    );

  const renderGallery = () => (
    <div className={styles.startViewWrapper}>
      <div className={styles.startViewHeader}>
        <div className={styles.startViewHeaderContainer}>
          {shouldShowOpenSeriesGalleryButton() &&
            renderOpenSeriesGalleryButton()}
          {platform.isKiosk && (
            <h1 className={styles.startViewApplicationName}>
              {APPLICATION_NAME}
            </h1>
          )}
          {renderHeader(translate(t.SPR_GALLERY_HEADLINE))}
        </div>

        <div className={styles.startViewHeaderContainer}>
          <div className={styles.startViewHeaderLanguageSelector}>
            {platform.isKiosk && !isUpptacka && <LanguageSelector />}
          </div>
          <div className={styles.startViewButtonWrapper}>
            {renderVpcInput()}
            {renderSprButton()}
          </div>
        </div>
      </div>
      <div className={styles.startView}>
        {renderStartFromBeginning()}
        {createProductCards()}
      </div>
    </div>
  );

  return renderGallery();
};

export default StartView;
